<template>
  <ElementFormItem
    :prop="inputKey"
    :rules="rules"
    :label="name"
    :error="customError || tagError"
    class="form-generator__content-item"
  >
    <StitchTooltip
      :message="disabledMessage"
      :is-disabled="!isDisabled"
    >
      <ElementSelect
        ref="selectRef"
        v-model="inputData"
        :placeholder="name"
        :popper-append-to-body="true"
        :disabled="isDisabled"
        popper-class="select-tags__hidden-dropdown"
        class="form-generator__select-input"
        :class="{ 'has-error': tagError }"
        :multiple="true"
        :allow-create="true"
        :default-first-option="true"
        filterable
        clearable
        @change="handleChange"
      >
        <ElementOption
          v-for="(option, keyOption) in options"
          :key="keyOption"
          :label="option.name"
          :value="option.id"
          data-testid="selectItem"
        />
      </ElementSelect>
    </StitchTooltip>

    <InputNotes
      v-if="notes.length > 0"
      :notes="notes"
    />
  </ElementFormItem>
</template>

<script>
import { InputMixin } from '../mixins/InputMixin.js'
import InputNotes from './InputNotes'

const ALPHA_ONLY = /^[A-Za-z]$/
const MAX_LENGTH = 3

export default {
  name: 'InputSelectTags',

  components: { InputNotes },

  mixins: [InputMixin],

  props: {
    customError: {
      type: String,
      required: false,
      default: null
    },
    defaultValue: {
      type: [Number, String, Array],
      required: false,
      default: null
    },
    options: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  data () {
    return {
      tagError: null
    }
  },

  mounted () {
    this.$nextTick().then(() => {
      this.attachInputListener()
    })
  },

  beforeDestroy () {
    this.detachInputListener()
  },

  methods: {
    /**
     */
    initInput () {
      this.inputData =
        this.defaultValue !== undefined ? this.defaultValue : null
      this.setData()
    },

    /**
     * @returns {object}
     */
    getInput () {
      return (
        this.$refs.selectRef?.$el.querySelector('.el-select__input') ||
        this.$refs.selectRef?.$el.querySelector('input') ||
        null
      )
    },

    /**
     */
    attachInputListener () {
      const input = this.getInput()

      if (input) {
        input.addEventListener('keydown', this.handleTagKeydown, true)
        input.addEventListener('input', this.handleTagInput, true)
      }
    },

    /**
     */
    detachInputListener () {
      const input = this.getInput()

      if (input) {
        input.removeEventListener('keydown', this.handleTagKeydown, true)
        input.removeEventListener('input', this.handleTagInput, true)
      }
    },

    /**
     * Scrubs any non-alpha or over-length characters after input fires,
     * as a safety net for paste events.
     *
     * @param {InputEvent} e
     */
    handleTagInput (e) {
      const input = e.target
      const raw = input.value
      const cleaned = raw.replace(/[^A-Za-z]/g, '').slice(0, MAX_LENGTH)

      if (cleaned !== raw) {
        input.value = cleaned
        input.dispatchEvent(new Event('input', { bubbles: true }))
        this.tagError = 'Tags must be exactly 3 letters (A–Z)'
      }
    },

    /**
     * @param {KeyboardEvent} e
     */
    handleTagKeydown (e) {
      const input = e.target
      const currentVal = input.value

      // Let control keys through untouched
      if (
        [
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Tab',
          'Escape'
        ].includes(e.key)
      ) {
        return
      }

      // Enter — validate the full pending value before Element UI commits the pill
      if (e.key === 'Enter') {
        const trimmed = currentVal.trim()

        if (!trimmed) {
          return
        }

        if (
          trimmed.length !== MAX_LENGTH ||
          !trimmed.split('').every(c => ALPHA_ONLY.test(c))
        ) {
          e.preventDefault()
          e.stopPropagation()
          this.tagError = 'Tags must be exactly 3 letters (A–Z)'

          return
        }

        // Check for duplicate against existing pills (case-insensitive)
        const upper = trimmed.toUpperCase()
        const currentTags = Array.isArray(this.inputData)
          ? this.inputData.map(v =>
            (typeof v === 'string' ? v : String(v)).toUpperCase()
          )
          : []

        if (currentTags.includes(upper)) {
          e.preventDefault()
          e.stopPropagation()
          this.tagError = `"${upper}" is already added`
          this.highlightDuplicate(upper)

          return
        }

        this.tagError = null

        return
      }

      // Block anything that is not a single alpha character
      if (!ALPHA_ONLY.test(e.key)) {
        e.preventDefault()
        e.stopPropagation()
        this.tagError = 'Tags must be exactly 3 letters (A–Z)'

        return
      }

      // Block once the input already holds MAX_LENGTH characters
      if (currentVal.length >= MAX_LENGTH) {
        e.preventDefault()
        e.stopPropagation()
        this.tagError = 'Tags must be exactly 3 letters (A–Z)'

        return
      }

      // Valid keystroke — clear any standing error
      this.tagError = null
    },

    /**
     * @param {string} upper — already-uppercased value to flash green
     */
    highlightDuplicate (upper) {
      this.$nextTick().then(() => {
        const tagEls = this.$el.querySelectorAll('.el-tag')
        tagEls.forEach(tagEl => {
          const label = tagEl
            .querySelector('.el-select__tags-text')
            ?.textContent?.trim()

          if (label === upper) {
            tagEl.classList.add('is-duplicate')
            setTimeout(() => tagEl.classList.remove('is-duplicate'), 1500)
          }
        })
      })
    },

    /**
     */
    handleChange () {
      if (Array.isArray(this.inputData)) {
        const existingIds = this.options.map(o =>
          typeof o.id === 'string' ? o.id.toUpperCase() : o.id
        )
        const existingNames = this.options.map(o => o.name.toUpperCase())

        this.inputData = [
          ...new Set(
            this.inputData
              .map(value =>
                typeof value === 'string' ? value.toUpperCase() : value
              )
              .filter(
                value =>
                  typeof value !== 'string' ||
                  (!existingIds.includes(value) &&
                    !existingNames.includes(value))
              )
          )
        ]
      }

      this.tagError = null
      this.setData()
    }
  }
}
</script>

<style lang="scss">
.select-tags__hidden-dropdown {
  display: none !important;
}
</style>

<style lang="scss" scoped>
@import '../../scss/utils';

$z-index-level-1: 9999;

.form-generator__select-input {
  width: 100%;
  min-width: $input-width;
}

.form-generator__select-input.has-error {
  /deep/ .el-input__inner {
    border-color: $red;

    &:focus {
      border-color: $red;
    }
  }
}

/deep/ .el-tag.is-duplicate {
  color: $green;
  background-color: $green-light;
  border-color: $green;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;

  .el-tag__close {
    color: $green;

    &:hover {
      color: $white;
      background-color: $green;
    }
  }
}

.form-generator__content-item {
  /deep/ .el-form-item__error {
    position: relative;
    top: 0;
    z-index: $z-index-level-1;
    padding-top: spacing(1/2);
  }
}
</style>
